import { useMemo } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { ExternalLink, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DriveKeepActions } from '@/components/drives/DriveKeepActions';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { useDriveMirroredRefs } from '@/hooks/useDrivesQueries';
import { DRIVE_READ_REASON_LABELS, formatBytes, formatDriveDate } from '@/utils/drive-file-row';

type DriveFileCardItem = Extract<ConversationItem, { kind: 'drive_file_card' }>;

export interface ProvenanceDriveFileCardProps {
  item: DriveFileCardItem;
}

function openWebUrl(webUrl: string): void {
  window.open(webUrl, '_blank', 'noopener,noreferrer');
}

export function ProvenanceDriveFileCard({ item }: ProvenanceDriveFileCardProps) {
  const paths = useMemo(() => [item.path], [item.path]);
  const refsQuery = useDriveMirroredRefs(item.driveId, paths);
  const webUrl = item.webUrl;
  const meta = [item.path, formatDriveDate(item.modifiedAt), item.sizeBytes !== null ? formatBytes(item.sizeBytes) : null]
    .filter((part): part is string => part !== null)
    .join(' · ');
  const keepable = item.reason !== 'NOT_FOUND' && item.reason !== 'IS_FOLDER';

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0" />
        <p className="min-w-0 truncate font-medium">{item.fileName}</p>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {webUrl ? (
            <button
              type="button"
              onClick={() => openWebUrl(webUrl)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              aria-label={`Open in ${item.productLabel}`}
              title={`Open in ${item.productLabel}`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <Badge variant="outline">{item.productLabel}</Badge>
        </div>
      </div>
      <p className="mt-1 truncate text-xs text-muted-foreground">{meta}</p>
      {item.reason === 'OK' ? (
        <>
          {item.preview ? <p className="mt-2 line-clamp-2 whitespace-pre-wrap">{item.preview}</p> : null}
          <p className="mt-1 text-xs text-muted-foreground">{item.totalChars.toLocaleString()} characters</p>
        </>
      ) : (
        <p className="mt-2 text-muted-foreground">{DRIVE_READ_REASON_LABELS[item.reason]}</p>
      )}
      {keepable ? (
        <div className="mt-2 border-t border-border pt-2">
          <DriveKeepActions
            driveId={item.driveId}
            path={item.path}
            fileName={item.fileName}
            kept={resolveKeptRef(refsQuery.data, item.path, item.mirroredContentId, item.mirroredCode)}
          />
        </div>
      ) : null}
    </div>
  );
}
